import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Pencil, Printer, Send, ShieldCheck, Trash2 } from "lucide-react";
import { DocumentViewDialog, type DocumentViewAction } from "@/components/documents/DocumentViewDialog";
import { SignedIndicator } from "@/components/documents/SignedIndicator";
import { PrescriptionPrintPreview } from "./PrescriptionPrintPreview";
import { PrescriptionShareDrawer } from "./PrescriptionShareDrawer";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useSignPdf } from "@/hooks/useSignPdf";
import { useUserA1Certificate } from "@/hooks/useUserA1Certificate";
import { useRenderPdf } from "@/hooks/useRenderPdf";
import { downloadPdf, printPdf } from "@/lib/pdf/download";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type MedicalPrescriptionRow = Database["public"]["Tables"]["medical_prescriptions"]["Row"];
type MedicalPrescriptionItemRow = Database["public"]["Tables"]["medical_prescription_items"]["Row"];
type MedicalPrescriptionExamItemRow =
  Database["public"]["Tables"]["medical_prescription_exam_items"]["Row"];

type PrescriptionWithItems = MedicalPrescriptionRow & {
  issuer_address?: string | null;
  items: MedicalPrescriptionItemRow[];
  exam_items: MedicalPrescriptionExamItemRow[];
  vet?: { id: string; full_name: string | null; address?: string | null } | null;
  clinic?: { id: string; name: string | null } | null;
};

interface PrescriptionViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prescription: PrescriptionWithItems;
  onEdit?: () => void;
  onDelete?: () => void;
  isDeleting?: boolean;
  editLabel?: string;
  onSign?: () => Promise<void>;
  isSigning?: boolean;
  autoOpenShare?: boolean;
}

export const PrescriptionViewDialog = ({
  open,
  onOpenChange,
  prescription,
  onEdit,
  onDelete,
  isDeleting = false,
  editLabel,
  onSign,
  isSigning = false,
  autoOpenShare = false,
}: PrescriptionViewDialogProps) => {
  const { t } = useTranslation("prescriptions");
  const [isShareDrawerOpen, setIsShareDrawerOpen] = useState(false);
  const [isRefreshingSignedData, setIsRefreshingSignedData] = useState(false);
  const prescriptionSignedAt = (prescription as PrescriptionWithItems & { signed_at?: string | null }).signed_at;
  const [hasSignedVersion, setHasSignedVersion] = useState(Boolean(prescriptionSignedAt));
  const signPdf = useSignPdf();
  const { data: a1Certificate } = useUserA1Certificate();
  const hasCertificate = Boolean(a1Certificate);

  const isSigned = hasSignedVersion || Boolean(prescriptionSignedAt);

  const { data: prescriptionWithPet, refetch: refetchPrescriptionWithPet } = useQuery({
    queryKey: ["prescription-with-pet", prescription.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("medical_prescriptions")
        .select(
          `
          *,
          items:medical_prescription_items(*),
          exam_items:medical_prescription_exam_items(*),
          vet:profiles!medical_prescriptions_vet_user_id_fkey(id, full_name, phone, address),
          clinic:clinics(id, name),
          pets:pets(
            id,
            name,
            birth_date,
            species:species(name),
            breed:breeds(name),
            gender:pet_genders(name),
            owners:owners(
              id,
              name,
              document,
              phones:owners_phones(id, phone_number, is_whatsapp, is_primary, deleted_at),
              addresses:owners_addresses(street, number, neighborhood, is_primary, deleted_at, city:cities(name, state:states(code)))
            ),
            weights:pet_weights(weight, measured_at, medical_record_id, deleted_at)
          )
        `
        )
        .eq("id", prescription.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: open && !!prescription.id,
  });

  const fullPrescription = prescriptionWithPet
    ? { ...prescription, ...prescriptionWithPet }
    : prescription;

  const renderPdf = useRenderPdf();

  // Unsigned PDF rendered by the PDF Lambda (same pipeline as the signed one). Callers toast on failure.
  const renderUnsignedPdf = useCallback(
    () => renderPdf.mutateAsync({ resource_type: "prescription", resource_id: prescription.id }),
    [renderPdf, prescription.id],
  );

  const handleBrowserPrint = useCallback(async () => {
    const bytes = await renderUnsignedPdf();
    printPdf(bytes);
  }, [renderUnsignedPdf]);

  const shareReady = Boolean(
    (fullPrescription as PrescriptionWithItems & {
      signed_at?: string | null;
      signed_storage_path?: string | null;
      access_code?: string | null;
    }).signed_at &&
    (fullPrescription as PrescriptionWithItems & { signed_storage_path?: string | null }).signed_storage_path &&
    (fullPrescription as PrescriptionWithItems & { access_code?: string | null }).access_code,
  );

  useEffect(() => {
    setHasSignedVersion(Boolean(prescriptionSignedAt));
  }, [open, prescription.id, prescriptionSignedAt]);

  const waitForSignedShareData = useCallback(async () => {
    setIsRefreshingSignedData(true);
    try {
      for (let attempt = 0; attempt < 5; attempt += 1) {
        const result = await refetchPrescriptionWithPet();
        const nextPrescription = result.data
          ? { ...prescription, ...result.data }
          : prescription;
        const ready = Boolean(
          (nextPrescription as PrescriptionWithItems & {
            signed_at?: string | null;
            signed_storage_path?: string | null;
            access_code?: string | null;
          }).signed_at &&
          (nextPrescription as PrescriptionWithItems & { signed_storage_path?: string | null }).signed_storage_path &&
          (nextPrescription as PrescriptionWithItems & { access_code?: string | null }).access_code,
        );

        if (ready) return true;
        await new Promise((resolve) => window.setTimeout(resolve, 400));
      }

      return false;
    } finally {
      setIsRefreshingSignedData(false);
    }
  }, [prescription, refetchPrescriptionWithPet]);

  const handleInternalSign = async () => {
    const result = await signPdf.mutateAsync({
      resource_type: "prescription",
      resource_id: prescription.id,
      pet_id: prescription.pet_id,
      appointment_id: prescription.appointment_id,
      medical_record_id: prescription.medical_record_id,
    });

    const secretCode = result.secret_code ?? result.access_code;
    if (secretCode) {
      toast.success(
        t("sign.successWithCode", {
          defaultValue: "Receita assinada!",
          code: secretCode,
        }),
      );
    }

    setHasSignedVersion(true);
  };

  const effectiveOnSign = !isSigned
    ? onSign ?? (hasCertificate ? handleInternalSign : undefined)
    : undefined;
  const isEffectiveSigning = isSigning || signPdf.isPending;

  const handleSignAction = async () => {
    if (!effectiveOnSign) return;
    await effectiveOnSign();
    setHasSignedVersion(true);
    await waitForSignedShareData();
  };

  const handleOpenShare = useCallback(async () => {
    if (!shareReady) {
      const ready = await waitForSignedShareData();
      if (!ready) {
        toast.error(t("share.sendError"));
        return;
      }
    }

    setIsShareDrawerOpen(true);
  }, [shareReady, t, waitForSignedShareData]);

  useEffect(() => {
    if (open && autoOpenShare) {
      void handleOpenShare();
    }
  }, [autoOpenShare, handleOpenShare, open]);

  const fetchSignedPdfBytes = async () => {
    type WithSigned = PrescriptionWithItems & {
      signed_storage_path?: string | null;
      signed_storage_bucket?: string | null;
    };

    let path = (fullPrescription as WithSigned).signed_storage_path;
    let bucket = ((fullPrescription as WithSigned).signed_storage_bucket) ?? "signed-pdfs";

    if (!path && isSigned) {
      const { data } = await refetchPrescriptionWithPet();
      if (data) {
        const merged = { ...prescription, ...data } as WithSigned;
        path = merged.signed_storage_path ?? null;
        bucket = merged.signed_storage_bucket ?? "signed-pdfs";
      }
    }

    if (!path) throw new Error("signed_path_missing");

    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, 60);

    if (error || !data?.signedUrl) throw error ?? new Error("url_failed");

    const response = await fetch(data.signedUrl);
    if (!response.ok) throw new Error("download_failed");

    return new Uint8Array(await response.arrayBuffer());
  };

  const handleDownloadVariant = async (variant: "signed" | "unsigned") => {
    try {
      const bytes = variant === "unsigned" ? await renderUnsignedPdf() : await fetchSignedPdfBytes();
      const fileName = variant === "signed"
        ? `receita_assinada_${prescription.id.slice(0, 8)}.pdf`
        : `receita_${prescription.id.slice(0, 8)}.pdf`;
      downloadPdf(bytes, fileName);
    } catch (error) {
      console.error(error);
      toast.error(
        variant === "signed"
          ? t("sign.downloadError", { defaultValue: "Erro ao baixar PDF assinado" })
          : t("sign.downloadUnsignedError", { defaultValue: "Erro ao baixar PDF" }),
      );
    }
  };

  const [printingVariant, setPrintingVariant] = useState<"signed" | "unsigned" | null>(null);

  const handlePrintVariant = async (variant: "signed" | "unsigned") => {
    if (printingVariant) return;
    setPrintingVariant(variant);
    try {
      if (variant === "unsigned") {
        await handleBrowserPrint();
        return;
      }
      const bytes = await fetchSignedPdfBytes();
      printPdf(bytes);
    } catch (error) {
      console.error(error);
      toast.error(
        variant === "signed"
          ? t("sign.printSignedError", { defaultValue: "Erro ao abrir PDF assinado para impressão" })
          : t("sign.printUnsignedError", { defaultValue: "Erro ao abrir PDF para impressão" }),
      );
    } finally {
      setPrintingVariant(null);
    }
  };

  if (!open) return null;

  const actions: DocumentViewAction[] = [
    ...(!isSigned && effectiveOnSign
      ? [
          {
            key: "sign",
            label: isEffectiveSigning
              ? t("sign.signing", { defaultValue: "Assinando..." })
              : t("sign.button", { defaultValue: "Assinar" }),
            icon: isEffectiveSigning ? Loader2 : ShieldCheck,
            onClick: () => void handleSignAction(),
            disabled: isEffectiveSigning,
            loading: isEffectiveSigning,
            variant: "default" as const,
          },
        ]
      : []),
    ...(isSigned
      ? [
          {
            key: "share",
            label: isRefreshingSignedData
              ? t("share.preparing", { defaultValue: "Preparando envio..." })
              : t("share.send", { defaultValue: "Enviar" }),
            icon: isRefreshingSignedData ? Loader2 : Send,
            onClick: () => void handleOpenShare(),
            disabled: isRefreshingSignedData,
            loading: isRefreshingSignedData,
            variant: "default" as const,
          },
        ]
      : []),
    ...(isSigned
      ? []
      : [
          {
            key: "print-unsigned",
            label:
              printingVariant === "unsigned"
                ? t("sign.printPreparing", { defaultValue: "Preparando impressão..." })
                : t("sign.print", { defaultValue: "Imprimir" }),
            icon: printingVariant === "unsigned" ? Loader2 : Printer,
            onClick: () => void handlePrintVariant("unsigned"),
            disabled: Boolean(printingVariant),
            loading: printingVariant === "unsigned",
            variant: "outline" as const,
          },
        ]),
    ...(isSigned
      ? [
          {
            key: "print-unsigned",
            label:
              printingVariant === "unsigned"
                ? t("sign.printPreparing", { defaultValue: "Preparando impressão..." })
                : t("sign.printWithoutSignature", { defaultValue: "Sem assinatura digital" }),
            icon: printingVariant === "unsigned" ? Loader2 : Printer,
            onClick: () => void handlePrintVariant("unsigned"),
            disabled: Boolean(printingVariant),
            loading: printingVariant === "unsigned",
            secondary: true,
          },
          {
            key: "print-signed",
            label:
              printingVariant === "signed"
                ? t("sign.printPreparing", { defaultValue: "Preparando impressão..." })
                : t("sign.printWithSignature", { defaultValue: "Com assinatura digital" }),
            icon: printingVariant === "signed" ? Loader2 : Printer,
            onClick: () => void handlePrintVariant("signed"),
            disabled: Boolean(printingVariant),
            loading: printingVariant === "signed",
            secondary: true,
          },
        ]
      : []),
    {
      key: "download-unsigned",
      label: isSigned
        ? t("sign.downloadWithoutSignature", { defaultValue: "Sem assinatura digital" })
        : t("sign.download", { defaultValue: "Baixar" }),
      icon: Download,
      onClick: () => void handleDownloadVariant("unsigned"),
      secondary: true,
      separatorBefore: isSigned,
    },
    ...(isSigned
      ? [
          {
            key: "download-signed",
            label: t("sign.downloadWithSignature", { defaultValue: "Com assinatura digital" }),
            icon: Download,
            onClick: () => void handleDownloadVariant("signed"),
            secondary: true,
          },
        ]
      : []),
    ...(!isSigned && onEdit
      ? [
          {
            key: "edit",
            label: editLabel ?? t("sign.edit", { defaultValue: "Editar" }),
            icon: Pencil,
            onClick: onEdit,
            secondary: true,
          },
        ]
      : []),
    ...(!isSigned && onDelete
      ? [
          {
            key: "delete",
            label: isDeleting
              ? t("sign.removing", { defaultValue: "Removendo..." })
              : t("sign.delete", { defaultValue: "Excluir" }),
            icon: Trash2,
            onClick: onDelete,
            disabled: isDeleting,
            secondary: true,
            separatorBefore: true,
            variant: "ghost" as const,
          },
        ]
      : []),
  ];

  return (
    <>
      <DocumentViewDialog
        open={open}
        onOpenChange={onOpenChange}
        variant="fullscreen"
        title={t("viewDialog.title", { defaultValue: "Visualização da receita" })}
        statusBadge={isSigned ? <SignedIndicator /> : undefined}
        subtitle={
          (fullPrescription as PrescriptionWithItems & { pets?: { name?: string | null } | null }).pets?.name
            ? t("viewDialog.patientContext", {
                defaultValue: "Paciente: {{name}}",
                name: (fullPrescription as PrescriptionWithItems & { pets?: { name?: string | null } | null }).pets
                  ?.name,
              })
            : t("viewDialog.previewDescription", {
                defaultValue: "Confira a receita e escolha a próxima ação.",
              })
        }
        actions={actions}
        moreActionsLabel={t("viewDialog.moreActions", { defaultValue: "Mais ações" })}
      >
        <PrescriptionPrintPreview prescription={fullPrescription} signed={isSigned} />
      </DocumentViewDialog>
      <PrescriptionShareDrawer
        open={isShareDrawerOpen}
        onOpenChange={setIsShareDrawerOpen}
        prescription={fullPrescription}
        onRequireSign={effectiveOnSign}
        isSigning={isEffectiveSigning}
      />
    </>
  );
};
